import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CONTACT, ASSETS } from "@/lib/siteData";
import { RevealLines, FadeUp } from "./Shared";

const facts = [
  { k: "Education", v: "BA (Hons.), MA, LLB" },
  { k: "Bar Enrolment", v: "Bihar State Bar Council" },
  { k: "Experience", v: "26+ Years of Practice" },
  { k: "Specialisation", v: "Civil & Revenue Cases" },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} id="about" data-testid="about-section" className="relative px-6 md:px-12 py-24 md:py-40">
      <div className="mx-auto max-w-[1500px]">
        <FadeUp className="flex items-baseline gap-4 mb-16 md:mb-24 border-b border-[var(--line)] pb-6">
          <span className="font-mono-plex text-xs text-[var(--muted)]">01</span>
          <span className="font-mono-plex text-xs uppercase tracking-[0.28em] text-[var(--muted)]">The Counsel</span>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* portrait */}
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden aspect-[4/5] bg-[var(--off)]">
              <motion.img
                style={{ y: imgY, scale: 1.12 }}
                src={ASSETS.portrait}
                alt="Adv. Sanjay Kr Sinha"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-[var(--line)]" />
            </div>
            <div className="mt-4 flex justify-between font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
              <span>{CONTACT.name}</span>
              <span>Fig. 01</span>
            </div>
          </div>

          {/* text */}
          <div className="lg:col-span-7 lg:pt-6">
            <RevealLines
              tag="h2"
              className="font-serif-display font-light leading-[0.95] text-4xl md:text-6xl tracking-tight mb-10"
              lines={["Counsel rooted in", "diligence, discretion", "and district practice."]}
            />
            <FadeUp delay={0.1}>
              <p className="text-base md:text-lg text-[var(--off)]/80 leading-relaxed max-w-2xl mb-6">
                For over twenty-six years, {CONTACT.name} has represented clients across civil and revenue disputes in Purnia. His practice is built on a meticulous reading of records, patient counsel and a steady presence in court.
              </p>
              <p className="text-base text-[var(--off)]/60 leading-relaxed max-w-2xl">
                From contested land titles to succession and eviction, each matter is handled with the same rigour — clear advice, honest timelines, and unwavering advocacy before the bench.
              </p>
            </FadeUp>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--line)] mt-14 border border-[var(--line)]">
              {facts.map((f, i) => (
                <motion.div
                  key={f.k}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  className="bg-[#060606] p-6 md:p-8"
                >
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.24em] text-[var(--muted)] mb-3">{f.k}</div>
                  <div className="font-serif-display text-xl md:text-2xl">{f.v}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
